import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import { Box, Paper, Typography, Chip, Alert, CircularProgress, Stack, Button, Link } from '@mui/material';
import { alpha } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';

type Status = 'INACTIVE' | 'TRIAL' | 'ACTIVE' | 'GRACE' | 'EXPIRED' | 'EXEMPT';

interface Subscription {
  status: Status;
  platform: string;
  trialEndsAt: string | null;
  trialRemainingDays: number | null;
  expiresAt: string | null;
  exempt: boolean;
  exemptReason: string | null;
  entitled: boolean;
  autoRenew: boolean;
}

interface CheckoutResult {
  checkoutUrl: string | null;
  alreadyActive: boolean;
}

interface PortalResult {
  portalUrl: string;
}

// カード情報の取扱い説明ページ（feast-corp /legal/payment/、feast-fe ADR 0037・0038）
const PAYMENT_SECURITY_URL = 'https://corp.feast.co.jp/legal/payment/';

// ポーリング上限。Stripe Checkout から戻った直後は webhook 反映待ちが長引くことがあるため 20 回（≒ 62s）。
const POLL_MAX = 20;

const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '取得に失敗しました。';
};

const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

// 状態ごとの表示ラベルと色。
const STATUS_LABEL: Record<Status, string> = {
  INACTIVE: '未課金',
  TRIAL: '無料トライアル中',
  ACTIVE: '課金中',
  GRACE: 'お支払い猶予中',
  EXPIRED: '期限切れ',
  EXEMPT: '課金免除（特権）',
};
const STATUS_COLOR: Record<Status, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  INACTIVE: 'default',
  TRIAL: 'warning',
  ACTIVE: 'success',
  GRACE: 'warning',
  EXPIRED: 'error',
  EXEMPT: 'info',
};

/**
 * サブスクリプション状況の表示。要件定義 0001 / feast-fe ADR 0011・0038。
 * BE: GET /stores/{storeId}/subscription。トライアルは残日数、EXEMPT は「特権（課金免除）」を強調表示する。
 * 購入は Stripe Checkout（ホスト型決済ページ）へのリダイレクト方式。カード変更は Stripe Customer Portal に委譲する。
 */
const SubscriptionSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 購入/解約リクエスト送信後、webhook 反映までの「受付済み・反映待ち」状態。
  // この間は購入/解約ボタンを隠して二重操作を防ぎ、受け付けた旨を表示する。
  const [pending, setPending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  // Checkout からの cancel 戻りやポーリング上限到達など、エラーではない案内の表示。
  const [notice, setNotice] = useState<{ severity: 'info' | 'warning'; text: string } | null>(null);
  // Stripe Checkout からの戻り query（?checkout=...）を一度だけ処理するためのガード。
  const handledReturnRef = useRef(false);

  const fetchStatus = useCallback(() => {
    if (!store_id) return Promise.resolve();
    return createApiRequest<Subscription>(`/stores/${store_id}/subscription`, { method: 'GET' })
      .then((data) => setSub(data))
      .catch((e) => setError(extractMessage(e)));
  }, [store_id]);

  const fetch = useCallback(() => {
    setLoading(true);
    fetchStatus().finally(() => setLoading(false));
  }, [fetchStatus]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // リクエスト送信後は webhook 反映にラグがあるため、目的状態になるまで数回ポーリングする。
  // pending=true の間は受付メッセージを表示し、操作ボタンを隠す（done 条件成立 or 上限到達で解除）。
  // 上限到達時（≒ 反映遅延）は再読み込みを促す案内を出す。
  const pollUntil = useCallback(
    (done: (s: Subscription) => boolean, message?: string) => {
      if (!store_id) return;
      setPending(true);
      setPendingMessage(message ?? null);
      let n = 0;
      const finish = (ok: boolean) => {
        setPending(false);
        setPendingMessage(null);
        if (!ok) {
          setNotice({ severity: 'warning', text: '反映に時間がかかっています。しばらくしてから画面を再読み込みしてください。' });
        }
      };
      const tick = () => {
        n += 1;
        createApiRequest<Subscription>(`/stores/${store_id}/subscription`, { method: 'GET' })
          .then((data) => {
            setSub(data);
            if (done(data)) finish(true);
            else if (n >= POLL_MAX) finish(false);
            else setTimeout(tick, 3000);
          })
          .catch(() => {
            if (n >= POLL_MAX) finish(false);
            else setTimeout(tick, 3000);
          });
      };
      setTimeout(tick, 2000);
    },
    [store_id],
  );

  // 購入完了は platform=WEB（Stripe 連携確定）を待つ。ACTIVE 判定にすると
  // トライアル中購入の終着（TRIAL + WEB = 課金開始予定）で永久に成立しないため。
  const purchaseDone = useCallback((s: Subscription) => s.platform === 'WEB', []);

  // Stripe Checkout からの戻り（?checkout=success|cancel）を処理し、query を除去する。
  useEffect(() => {
    if (handledReturnRef.current) return;
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    handledReturnRef.current = true;
    // session_id ごと query を除去（リロード時の再処理防止。history は置換）。
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    if (checkout === 'success') {
      pollUntil(purchaseDone, '決済処理を確認しています。反映まで少々お待ちください（自動で画面が更新されます）。');
    } else if (checkout === 'cancel') {
      setNotice({ severity: 'info', text: '購入手続きを中断しました。' });
    }
  }, [searchParams, setSearchParams, pollUntil, purchaseDone]);

  // 購入: Checkout セッションを作成し Stripe のホスト型決済ページへ遷移する。
  // alreadyActive（BE 側の生存確認で契約済みと判明）の場合は遷移せず反映を待つ。
  const handlePurchase = useCallback(() => {
    if (!store_id) return;
    setActionError(null);
    setNotice(null);
    setBusy(true);
    createApiRequest<CheckoutResult>(`/stores/${store_id}/subscription/web/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idempotencyKey: newIdempotencyKey() }),
    })
      .then((res) => {
        if (res.alreadyActive) {
          setBusy(false);
          pollUntil(purchaseDone);
          return;
        }
        if (res.checkoutUrl) {
          // Stripe の決済ページへ遷移（busy は解除しない: 遷移までの二重クリック防止）。
          window.location.assign(res.checkoutUrl);
          return;
        }
        setBusy(false);
        setActionError('決済ページの作成に失敗しました。時間をおいて再度お試しください。');
      })
      .catch((e) => {
        setBusy(false);
        setActionError(extractMessage(e));
      });
  }, [store_id, pollUntil, purchaseDone]);

  const handleCancel = useCallback(() => {
    if (!store_id) return;
    if (!window.confirm('Web サブスクリプションを解約しますか？（自動更新を停止します。データは残ります）')) return;
    setActionError(null);
    setNotice(null);
    setBusy(true);
    createApiRequest<void>(`/stores/${store_id}/subscription/web`, { method: 'DELETE' })
      // 解約反映を待つ: ACTIVE は auto_renew=false 化、TRIAL(未来開始)は platform が WEB→NONE へ戻る。
      .then(() => pollUntil((s) => !s.autoRenew || s.platform !== 'WEB'))
      .catch((e) => setActionError(extractMessage(e)))
      .finally(() => setBusy(false));
  }, [store_id, pollUntil]);

  const handleResume = useCallback(() => {
    if (!store_id) return;
    setActionError(null);
    setNotice(null);
    setBusy(true);
    createApiRequest<void>(`/stores/${store_id}/subscription/web/resume`, { method: 'POST' })
      // 再開反映を待つ（auto_renew=true 化）。
      .then(() => pollUntil((s) => s.autoRenew))
      .catch((e) => setActionError(extractMessage(e)))
      .finally(() => setBusy(false));
  }, [store_id, pollUntil]);

  // カード変更: Stripe Customer Portal（支払い方法の更新のみ有効）へ遷移する。
  const handleChangeCard = useCallback(() => {
    if (!store_id) return;
    setActionError(null);
    setNotice(null);
    setBusy(true);
    createApiRequest<PortalResult>(`/stores/${store_id}/subscription/web/portal`, { method: 'POST' })
      .then((res) => {
        // Portal へ遷移（busy は解除しない: 遷移までの二重クリック防止）。
        window.location.assign(res.portalUrl);
      })
      .catch((e) => {
        setBusy(false);
        setActionError(extractMessage(e));
      });
  }, [store_id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!sub) {
    return error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null;
  }

  // 特権（課金免除）は専用の目立つ表示にする。
  if (sub.exempt) {
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: (t) => alpha(t.palette.info.main, 0.08), border: '1px solid', borderColor: 'info.main', borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <VerifiedIcon color="info" />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>特権アカウント（課金免除）</Typography>
          <Chip size="small" color="info" label="EXEMPT" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          この店舗は課金が免除されています{sub.exemptReason ? `（${sub.exemptReason}）` : ''}。
        </Typography>
      </Paper>
    );
  }

  const trialNote =
    sub.status === 'TRIAL' && sub.trialRemainingDays != null
      ? sub.trialRemainingDays > 0
        ? `残り ${sub.trialRemainingDays} 日`
        : '本日まで'
      : null;

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">サブスクリプション</Typography>
        <Chip size="small" color={STATUS_COLOR[sub.status]} label={STATUS_LABEL[sub.status]} />
        {sub.status === 'ACTIVE' && sub.platform === 'WEB' && !sub.autoRenew && (
          <Chip size="small" color="warning" variant="outlined" label="解約予定" />
        )}
        {sub.status === 'TRIAL' && sub.platform === 'WEB' && (
          <Chip size="small" color="info" variant="outlined" label="課金開始予定" />
        )}
        {trialNote && (
          <Typography variant="body2" color={sub.trialRemainingDays === 0 ? 'error' : 'text.secondary'}>
            {trialNote}
          </Typography>
        )}
      </Stack>
      {(sub.status === 'EXPIRED' || sub.status === 'INACTIVE') && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          管理機能を続けてご利用いただくには、サブスクリプションを有効化してください（Web またはアプリ内課金）。
        </Typography>
      )}
      {sub.status === 'ACTIVE' && sub.platform === 'WEB' && !sub.autoRenew && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          自動更新を停止しました{sub.expiresAt ? `（${new Date(sub.expiresAt).toLocaleDateString('ja-JP')} まで利用可能）` : ''}。
        </Typography>
      )}
      {sub.status === 'TRIAL' && sub.platform === 'WEB' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          購入済みです。トライアル終了後{sub.trialEndsAt ? `（${new Date(sub.trialEndsAt).toLocaleDateString('ja-JP')}）` : ''}に課金が開始されます。解約するとトライアルに戻ります。
        </Typography>
      )}

      {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}
      {notice && !pending && <Alert severity={notice.severity} sx={{ mt: 1 }}>{notice.text}</Alert>}

      {/* リクエスト受付済み・反映待ち。二重操作防止のため操作ボタンは隠す。 */}
      {pending && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 1 }}>
          {pendingMessage ?? 'リクエストを受け付けました。反映まで少々お待ちください（自動で画面が更新されます）。'}
        </Alert>
      )}

      {/* Web（Stripe Checkout）購入導線。TRIAL/INACTIVE/EXPIRED で表示。購入済み（TRIAL+WEB=課金開始予定）は除く。 */}
      {!pending && ['TRIAL', 'INACTIVE', 'EXPIRED'].includes(sub.status) && !(sub.status === 'TRIAL' && sub.platform === 'WEB') && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" variant="contained" disabled={busy} onClick={handlePurchase}>
            Web で購入（月額¥3,300 税込）
          </Button>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            お支払いは決済代行サービス「Stripe」の安全な決済ページで処理され、カード情報が当社に保存されることはありません。
            詳しくは
            <Link href={PAYMENT_SECURITY_URL} target="_blank" rel="noopener" underline="hover">
              お支払いの安全性について
            </Link>
            をご覧ください。
          </Typography>
        </Box>
      )}

      {/* 解約導線: 課金中(自動更新オン) または トライアル中の購入済み(課金開始予定)。解約予定では出さない。
          課金中はカード変更（Stripe Customer Portal）も並べる。 */}
      {!pending && sub.platform === 'WEB' && ((sub.status === 'ACTIVE' && sub.autoRenew) || sub.status === 'TRIAL') && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={handleCancel}>
            解約する
          </Button>
          {sub.status === 'ACTIVE' && (
            <Button size="small" variant="outlined" disabled={busy} onClick={handleChangeCard}>
              お支払いカードを変更
            </Button>
          )}
        </Stack>
      )}

      {/* 解約予定（自動更新停止）は再開導線。期間満了前なら自動更新を戻せる。
          カード変更は Stripe Customer Portal に委譲（解約予定中も次回請求に備えて変更可能）。 */}
      {!pending && sub.status === 'ACTIVE' && sub.platform === 'WEB' && !sub.autoRenew && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" disabled={busy} onClick={handleResume}>
            自動更新を再開する
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={handleChangeCard}>
            お支払いカードを変更
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default SubscriptionSection;
